import { assign, setup } from "xstate";
import type { OnboardingConfig } from "../../lib/onboarding/flowConfig.fallback";
import { toEnum, toEnumArray } from "../../lib/onboarding/onboardingVocabulary";
import type { OnboardingAnswers } from "../../types/onboarding";
import {
	clearAnswers,
	clearedValue,
	dependentsToClear,
	fieldChanged,
} from "./cascadeInvalidation";
import {
	type FlowAnswers,
	isDonor,
	shouldSkipCycle,
	shouldSkipJourney,
	shouldSkipRole,
	shouldSkipSteps,
} from "./flowGuards";

interface Ctx {
	config: OnboardingConfig;
	answers: OnboardingAnswers;
	// Last-advanced value per field; updated on NEXT (not per ANSWER) so re-picking
	// the same value is a no-op. Drives cascade-invalidation change detection.
	committed: OnboardingAnswers;
	history: string[];
	backTarget: string | null;
}

type Events =
	| { type: "ANSWER"; field: string; value: string | string[] }
	| { type: "NEXT" }
	| { type: "BACK" };

// Commit the just-advanced screen's field into the `committed` baseline. When the
// value changed since it was last committed, clear its downstream dependents in
// both `answers` (so the next screen + skip-guards see clean state) and the
// baseline. No change → context returned untouched.
function applyCommit(
	context: Ctx,
	field: string,
): Pick<Ctx, "answers" | "committed"> {
	if (!fieldChanged(context.committed[field], context.answers[field])) {
		return { answers: context.answers, committed: context.committed };
	}
	const committed: OnboardingAnswers = {
		...context.committed,
		[field]: context.answers[field],
	};
	for (const dep of dependentsToClear(field)) {
		committed[dep] = clearedValue(dep);
	}
	return { answers: clearAnswers(context.answers, field), committed };
}

// Guards reason in canonical enums, but context stores the screens' raw label
// values. Normalize a snapshot for guard evaluation only (idempotent: already-
// canonical values pass through). Persisted legacy label values route correctly.
export function toFlowAnswers(answers: OnboardingAnswers): FlowAnswers {
	const g = String(answers.gender ?? "");
	const s = String(answers.fertilityStatus ?? "");
	const r = String(answers.fertilityRole ?? "");
	return {
		gender: g ? (toEnum("gender", g) ?? g) : undefined,
		fertilityStatus: s ? (toEnum("fertilityStatus", s) ?? s) : undefined,
		fertilityRole: r ? (toEnum("fertilityRole", r) ?? r) : undefined,
		fertilityJourneyTypes: Array.isArray(answers.fertilityJourneyTypes)
			? toEnumArray("journeyType", answers.fertilityJourneyTypes)
			: undefined,
	};
}

// Screens this machine routes between; extras kept around the v5 spine.
const BACK_TARGETS = [
	"setupProfile",
	"setupDetails",
	"status",
	"role",
	"journey",
	"steps",
	"cycle",
	"needs",
	"howDidYouHear",
	"additionalInfo",
] as const;

type ScreenId = (typeof BACK_TARGETS)[number];

// When resuming at `target` (crash/reload restore), rebuild the Back-history
// the user would have accumulated walking the spine: every screen before the
// target that would NOT have been auto-skipped given the seeded answers.
function synthesizeHistory(
	config: OnboardingConfig,
	answers: OnboardingAnswers,
	target: string | undefined,
): string[] {
	const idx = BACK_TARGETS.indexOf(target as ScreenId);
	if (idx <= 0) return [];
	const flow = toFlowAnswers(answers);
	const donor = isDonor(flow);
	const skipped = (id: ScreenId): boolean => {
		if (id === "role") return shouldSkipRole(config, flow);
		if (id === "journey") return donor || shouldSkipJourney(config, flow);
		if (id === "steps") return donor || shouldSkipSteps(config, flow);
		if (id === "cycle") return donor || shouldSkipCycle(config, flow);
		return false;
	};
	return BACK_TARGETS.slice(0, idx).filter((id) => !skipped(id));
}

export const onboardingMachine = setup({
	types: {
		context: {} as Ctx,
		events: {} as Events,
		input: {} as {
			config: OnboardingConfig;
			initialAnswers: OnboardingAnswers;
			initialScreen?: string;
			synthesizeBackHistory?: boolean;
		},
	},
	guards: {
		skipRole: ({ context }) =>
			shouldSkipRole(context.config, toFlowAnswers(context.answers)),
		skipJourney: ({ context }) =>
			isDonor(toFlowAnswers(context.answers)) ||
			shouldSkipJourney(context.config, toFlowAnswers(context.answers)),
		skipSteps: ({ context }) =>
			isDonor(toFlowAnswers(context.answers)) ||
			shouldSkipSteps(context.config, toFlowAnswers(context.answers)),
		skipCycle: ({ context }) =>
			isDonor(toFlowAnswers(context.answers)) ||
			shouldSkipCycle(context.config, toFlowAnswers(context.answers)),
		backTo: ({ context }, params: { id: string }) =>
			context.backTarget === params.id,
	},
	actions: {
		applyAnswer: assign({
			answers: ({ context, event }) => {
				if (event.type !== "ANSWER") return context.answers;
				return { ...context.answers, [event.field]: event.value };
			},
		}),
		pushHistory: assign({
			history: ({ context }, params: { id: string }) => [
				...context.history,
				params.id,
			],
		}),
		popHistory: assign({
			backTarget: ({ context }) =>
				context.history[context.history.length - 1] ?? null,
			history: ({ context }) => context.history.slice(0, -1),
		}),
		commitField: assign(({ context }, params: { field: string }) =>
			applyCommit(context, params.field),
		),
	},
}).createMachine({
	id: "onboarding",
	context: ({ input }) => {
		// Resume support: when a persisted screen id is provided, enter via the
		// transient `back` router (initial state below) with a synthesized
		// history so BACK behaves as if the user had walked the spine.
		const restore = BACK_TARGETS.includes(input.initialScreen as ScreenId)
			? input.initialScreen
			: undefined;
		return {
			config: input.config,
			// Distinct copies: answers and committed must not alias the same object
			// (or each other), so change detection can't desync via shared refs.
			answers: { ...input.initialAnswers },
			committed: { ...input.initialAnswers },
			history: restore
				? input.synthesizeBackHistory === false
					? []
					: synthesizeHistory(input.config, input.initialAnswers, restore)
				: [],
			backTarget: restore ?? null,
		};
	},
	initial: "back",
	states: {
		setupProfile: {
			on: {
				NEXT: {
					target: "setupDetails",
					actions: [{ type: "pushHistory", params: { id: "setupProfile" } }],
				},
			},
		},
		setupDetails: {
			on: {
				NEXT: {
					target: "status",
					actions: [
						{ type: "commitField", params: { field: "gender" } },
						{ type: "pushHistory", params: { id: "setupDetails" } },
					],
				},
				BACK: { target: "back", actions: "popHistory" },
			},
		},
		status: {
			on: {
				NEXT: {
					target: "role",
					actions: [
						{ type: "commitField", params: { field: "fertilityStatus" } },
						{ type: "pushHistory", params: { id: "status" } },
					],
				},
				BACK: { target: "back", actions: "popHistory" },
			},
		},
		role: {
			always: [{ guard: "skipRole", target: "journey" }],
			on: {
				NEXT: {
					target: "journey",
					actions: [
						{ type: "commitField", params: { field: "fertilityRole" } },
						{ type: "pushHistory", params: { id: "role" } },
					],
				},
				BACK: { target: "back", actions: "popHistory" },
			},
		},
		journey: {
			always: [{ guard: "skipJourney", target: "steps" }],
			on: {
				NEXT: {
					target: "steps",
					actions: [
						{ type: "commitField", params: { field: "fertilityJourneyTypes" } },
						{ type: "pushHistory", params: { id: "journey" } },
					],
				},
				BACK: { target: "back", actions: "popHistory" },
			},
		},
		steps: {
			always: [{ guard: "skipSteps", target: "cycle" }],
			on: {
				NEXT: {
					target: "cycle",
					actions: [{ type: "pushHistory", params: { id: "steps" } }],
				},
				BACK: { target: "back", actions: "popHistory" },
			},
		},
		cycle: {
			always: [{ guard: "skipCycle", target: "needs" }],
			on: {
				NEXT: {
					target: "needs",
					actions: [{ type: "pushHistory", params: { id: "cycle" } }],
				},
				BACK: { target: "back", actions: "popHistory" },
			},
		},
		needs: {
			on: {
				NEXT: {
					target: "howDidYouHear",
					actions: [{ type: "pushHistory", params: { id: "needs" } }],
				},
				BACK: { target: "back", actions: "popHistory" },
			},
		},
		howDidYouHear: {
			on: {
				NEXT: {
					target: "additionalInfo",
					actions: [{ type: "pushHistory", params: { id: "howDidYouHear" } }],
				},
				BACK: { target: "back", actions: "popHistory" },
			},
		},
		additionalInfo: {
			on: {
				NEXT: {
					target: "done",
					actions: [{ type: "pushHistory", params: { id: "additionalInfo" } }],
				},
				BACK: { target: "back", actions: "popHistory" },
			},
		},
		// Transient: route BACK to the popped screen id via guarded always edges.
		back: {
			always: [
				...BACK_TARGETS.map((id) => ({
					guard: { type: "backTo" as const, params: { id } },
					target: id,
				})),
				{ target: "setupProfile" },
			],
		},
		done: { type: "final" },
	},
	on: {
		ANSWER: { actions: "applyAnswer" },
	},
});
